import { t } from "@travily/i18n";
import { Icon } from "@ui/components/Icon";
import { OutlineButton } from "@ui/components/OutlineButton";
import { useBreakpoint } from "@ui/hooks/use-breakpoint";
import { useHasMounted } from "@ui/hooks/use-has-mounted";
import { openDockedChat } from "@/lib/message-dock-store";
import { messageThreadHref } from "@/lib/routes";

export interface MessageButtonProps {
  /** Whether a desktop press may dock the chat; false keeps the CTA a link to the messages page. */
  canDock?: boolean;
  /** Pre-seeds the composer with an enquiry the user still has to send. */
  draft?: string;
  handle: string;
  size?: "36" | "40";
}

/**
 * The purple-accented "start chat" affordance — an OutlineButton with the paperPlane icon. Docks
 * the chat on desktop, matching MessageDock's own `lg` gate; below it the messages page is the
 * only chat surface, so the CTA stays a link there.
 */
export function MessageButton({ canDock = true, draft, handle, size = "40" }: MessageButtonProps) {
  const isDesktop = useBreakpoint("lg");
  // `useBreakpoint` answers `true` on the server, so without the mount gate a server-rendered pass
  // would emit the dock's <button> — inert until hydration, and a mismatch against the link the
  // client renders below `lg`.
  const hasMounted = useHasMounted();

  const buttonProps = {
    children: t("components.views.shared.message_button.start_chat"),
    className: "whitespace-nowrap",
    hoverColor: "purple",
    leadIcon: <Icon name="paperPlane" size="20" />,
    size,
  } as const;

  const href = messageThreadHref(handle, draft);
  if (!canDock || !hasMounted || !isDesktop) return <OutlineButton {...buttonProps} href={href} />;

  // Opening is all this does: MessageDock owns fetching the thread, and sends the reader on to the
  // messages page — login and all — whenever the server will not serve it here.
  return <OutlineButton {...buttonProps} onPress={() => openDockedChat(handle, draft)} />;
}
